import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotReadyIcon from '@mui/icons-material/DoNotDisturbAlt';
import OpenedIcon from '@mui/icons-material/KeyboardArrowDown';
import ClosedIcon from '@mui/icons-material/KeyboardArrowUp';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useHistory, useLocation } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import cx from 'classnames';

import {
  buildQueryString,
  findSearchValue,
  getAllContentSortOptionAndType,
  getSharedWithMePageSortOptionAndType,
  parseQueryString,
} from '../../../helpers';
import useOnClickOutside from '../../../helpers/useOnClickOutside';
import {
  bypassCacheForNextVideofeedsListFetch,
  deleteVideoAngle,
  updateVideoTitle,
} from '../../../api/videofeeds';
import { getUserOrganization } from '../../../api/organizationsApi';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { NOTIFICATION, STATUS, VIDEOFEED_STATUS, VIDEOFEED_TYPE } from '../../../enums/ENUMS';
import { ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { SortOptions, SortType } from '../../../enums/SORT';
import { RolesLowerCase } from '../../../enums/ROLES';
import { VIDEOFEED, VIDEO_TYPE } from '../../../enums/TYPES';
import styles from './VideoFileList.module.scss';
import {
  clearComposerData,
  triggerRefresh as triggerComposerRefresh,
  updateComposerPixelPosition,
  updateComposerPlayControls,
  updateProgressPosition,
} from '../../../store/composers/composersSlice';
import LinearProgress from '../LinearProgress';
import FullPageLoader from '../FullPageLoader';
import Modal from '../Modal';
import { ContainedButton, OutlinedButton } from '../Buttons';
import { PATHS } from '../../../enums/PATHS';
import {
  clearVideoFeeds,
  getAllContentFolderThunk,
  getAllContentVideofeedsThunk,
  getAllSharedVideoFeedsByFolderId,
  getAllVideoFeedsByFolderId,
  getFolderByIdThunk,
  getSharedFolderByIdThunk,
  removeStoreSelectedClip,
  saveClipToStore,
  triggerRefresh,
  updateNotes,
  updateQualifiers,
  updateSortedSelectedClips,
  updateTimelineSeconds,
  updateVideoControls,
  updateVideoPlayedSeconds,
} from '../../../store/videos/videoSlice';
import { refetchFeeds } from '../../../store/videoFeedsList/videoFeedsListSlice';
import { saveMyOrganization } from '../../../store/user/userSlice';
import {
  ADDITIONAL_ANGLE_PLACEHOLDER_TTL,
  emptyComposerStackAction,
  UploadingStatus,
  updateAdditionalAngleUploadFeed,
  updateUploadingContentState,
} from '../../../store/sharedStore/sharedStoreSlice';
import { ReactComponent as ComposerIcon } from '../../../assets/icons/composer.svg';
import { ReactComponent as MovieIcon } from '../../../assets/icons/movieCollection.svg';
import { ReactComponent as TimelineIcon } from '../../../assets/icons/timeline.svg';
import FolderIcon from '../../../assets/icons/folder-open.svg';
import deleteModalStyles from '../../VideofeedsList/DeleteVideofeedModal/DeleteVideofeedModal.module.scss';

const VideoFileList = () => {
  const dispatch = useAppDispatch();
  const { videoFeed, status, parentFolder, refreshTrigger, storeSelectedClip, videoPlayedSeconds, videoControls } = useAppSelector(
    (state) => state.videoStore,
  );
  const { refreshTrigger: composerRefreshTrigger, composerPlayControl, composerVideoClips, currentComposerVideos } = useAppSelector(
    (state) => state.composersStore,
  );
  const { selectedVideoQuality, showSidebar, uploadingContentState, currentVideoThatInFrame } = useAppSelector(
    (state) => state.sharedStore,
  );
  const { refetchFeedList } = useAppSelector((state) => state.videoFeedsListStore);
  const { currentUser } = useAppSelector((state) => state.user);
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const folderId = searchParams.get('folderId');
  const feedId = findSearchValue('feedId');
  const fileside = findSearchValue('fileside');
  const shared = findSearchValue('shared');

  const allContent = findSearchValue('allContent');
  const isContentAdminOA = allContent === 'true' && currentUser?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN;
  const notification = useSnackbar();
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [angleContextMenu, setAngleContextMenu] = useState<{
    feedId?: string;
    videoId: string;
    mode: 'rename' | 'additional-angle';
    x: number;
    y: number;
  } | null>(null);
  const [angleToDelete, setAngleToDelete] = useState<{
    feedId: string;
    videoId: string;
    name: string;
  } | null>(null);
  const [deletingAngle, setDeletingAngle] = useState(false);
  const [editingVideoId, setEditingVideoId] = useState<string | null>(null);
  const [editingAngleName, setEditingAngleName] = useState('');
  const [savingVideoId, setSavingVideoId] = useState<string | null>(null);

  const feedRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const angleRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const angleMenuRef = useRef<HTMLDivElement | null>(null);
  const angleInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const videoIds = new Set<string>();
    videoFeed.forEach((feed) => {
      [feed, ...(feed.reference_timelines || [])].forEach((item) => {
        (item.videos || []).forEach((video) => {
          if (String((video as any).status || '').toUpperCase() === 'TRANSCODED') {
            videoIds.add(String(video.id));
          }
        });
      });
    });

    const now = Date.now();
    const nextUploadingState = uploadingContentState.filter(
      (item) =>
        item.uploadMode !== 'additional-angle' ||
        !(
          (item.videoId && videoIds.has(String(item.videoId))) ||
          (item.createdAt && now - item.createdAt >= ADDITIONAL_ANGLE_PLACEHOLDER_TTL)
        ),
    );

    if (nextUploadingState.length !== uploadingContentState.length) {
      dispatch(updateUploadingContentState(nextUploadingState));
    }
  }, [dispatch, uploadingContentState, videoFeed]);

  const pendingAdditionalAngleFeedIds = useMemo(() => {
    const feedIds = new Set(
      uploadingContentState
        .filter(
          (item) =>
            item?.uploadMode === 'additional-angle' &&
            item?.status !== UploadingStatus.FAILED &&
            item?.status !== UploadingStatus.FINISHED,
        )
        .map((item) => String(item.videofeedId)),
    );
    videoFeed.forEach((feed) => {
      if (feedIds.has(String(feed.id))) {
        (feed.reference_timelines || []).forEach((timeline) => feedIds.add(String(timeline.id)));
      }
    });
    return feedIds;
  }, [uploadingContentState, videoFeed]);

  const doneFeeds = useMemo(() => {
    return videoFeed.filter(
      (feed) =>
        feed?.status === VIDEOFEED_STATUS.DONE ||
        pendingAdditionalAngleFeedIds.has(String(feed.id)) ||
        String(feed.id) === String(feedId),
    );
  }, [videoFeed, pendingAdditionalAngleFeedIds, feedId]);

  const groupedSidebarFeeds = useMemo(() => {
    type TDoneFeed = (typeof doneFeeds)[number];
    type TSidebarRenderItem =
      | { key: string; kind: 'collection'; feed: TDoneFeed }
      | {
          key: string;
          kind: 'feed';
          feed: TDoneFeed;
          isReference: boolean;
          belongsToCollection?: boolean;
        };

    const getParentId = (feed: TDoneFeed) => {
      return feed?.parent_videofeed_id ?? (feed as any)?.parent_videofeed?.id ?? null;
    };

    const feedsById = new Map<string, TDoneFeed>();
    doneFeeds.forEach((feed) => {
      feedsById.set(String(feed.id), feed);
    });
    doneFeeds.forEach((feed) => {
      (feed?.reference_timelines || []).forEach((timeline) => {
        if (!timeline) return;
        const timelineId = String(timeline.id);
        if (!feedsById.has(timelineId)) {
          feedsById.set(timelineId, timeline as TDoneFeed);
        }
      });
    });
    const normalizedDoneFeeds = Array.from(feedsById.values());
    const feedIds = new Set(normalizedDoneFeeds.map((feed) => String(feed.id)));

    const childrenByParentId = new Map<string, TDoneFeed[]>();
    normalizedDoneFeeds.forEach((feed) => {
      const parentId = getParentId(feed);
      if (parentId === null || parentId === undefined) return;
      const key = String(parentId);
      const items = childrenByParentId.get(key) || [];
      items.push(feed);
      childrenByParentId.set(key, items);
    });

    const parentIds = new Set<string>();
    const groupedReferenceIds = new Set<string>();
    const items: TSidebarRenderItem[] = [];

    normalizedDoneFeeds.forEach((feed) => {
      if (groupedReferenceIds.has(String(feed.id))) return;

      const parentId = getParentId(feed);
      const hasCollectionTimelinesFromBackend = (feed?.reference_timelines || []).length > 0;
      const isTopLevelTimelineWithTopics =
        parentId === null &&
        feed.type === VIDEOFEED_TYPE.TIMELINE &&
        (feed?.topics?.length || 0) > 0;
      if (parentId !== null && parentId !== undefined && !hasCollectionTimelinesFromBackend) {
        if (!feedIds.has(String(parentId))) {
          items.push({ key: `feed-${feed.id}`, kind: 'feed', feed, isReference: false });
        }
        return;
      }

      const refsFromParentLinks = childrenByParentId.get(String(feed.id)) || [];
      const refsFromNested = (feed?.reference_timelines || []).filter((timeline) => String(timeline?.id) !== String(feed.id));
      const refsById = new Map<string, TDoneFeed>();

      refsFromParentLinks.forEach((referenceFeed) => refsById.set(String(referenceFeed.id), referenceFeed));
      refsFromNested.forEach((referenceFeed) => refsById.set(String(referenceFeed.id), referenceFeed as TDoneFeed));
      const refs = Array.from(refsById.values());
      const isCollectionRoot = Boolean((feed as any)?.is_collection_root);
      const isCollectionShell = Boolean((feed as any)?.is_collection_shell);
      const treatAsCollection =
        isCollectionRoot || isCollectionShell || hasCollectionTimelinesFromBackend || refs.length > 0 || isTopLevelTimelineWithTopics;
      const isShellCollectionRoot =
        treatAsCollection &&
        !feed?.duplicate &&
        (feed?.topics?.length || 0) === 0 &&
        (refs.length > 0 || isCollectionRoot || isCollectionShell);

      if (treatAsCollection) {
        parentIds.add(String(feed.id));
        items.push({ key: `collection-${feed.id}`, kind: 'collection', feed });
        if (!isShellCollectionRoot) {
          items.push({
            key: `timeline-parent-${feed.id}`,
            kind: 'feed',
            feed,
            isReference: true,
            belongsToCollection: true,
          });
        }
        refs.forEach((referenceFeed) => {
          groupedReferenceIds.add(String(referenceFeed.id));
          items.push({
            key: `timeline-child-${feed.id}-${referenceFeed.id}`,
            kind: 'feed',
            feed: referenceFeed,
            isReference: true,
            belongsToCollection: true,
          });
        });
        return;
      }

      items.push({ key: `feed-${feed.id}`, kind: 'feed', feed, isReference: false });
    });

    return { items, parentIds };
  }, [doneFeeds]);

  const sidebarFeedById = useMemo(() => {
    const map = new Map<string, (typeof doneFeeds)[number]>();
    groupedSidebarFeeds.items.forEach((item) => {
      if (item.kind === 'feed') {
        map.set(String(item.feed.id), item.feed);
      }
    });
    videoFeed.forEach((feed) => {
      const feedId = String(feed.id);
      if (!map.has(feedId)) {
        map.set(feedId, feed as (typeof doneFeeds)[number]);
      }
    });
    return map;
  }, [groupedSidebarFeeds.items, videoFeed, doneFeeds]);

  const sidebarFeedIndexById = useMemo(() => {
    const indexById: Record<string, number> = {};
    const seen = new Set<string>();
    let index = 0;
    groupedSidebarFeeds.items.forEach((item) => {
      if (item.kind !== 'feed') return;
      const id = String(item.feed.id);
      if (seen.has(id)) return;
      seen.add(id);
      indexById[id] = index;
      index += 1;
    });
    return indexById;
  }, [groupedSidebarFeeds.items]);

  const isFolderCollaborator =
    !!currentUser &&
    (parentFolder?.collaborators?.some((user) => user.email === currentUser.email) ||
      !!parentFolder?.group_collaborators?.some(
        (group) =>
          group.analysts?.some((user) => user.email === currentUser.email) ||
          group.viewers?.some((user) => user.email === currentUser.email),
      ));

  const canRenameAngle = (feed: VIDEOFEED) => {
    if (!showSidebar || !currentUser) {
      return false;
    }

    const isFeedOwner = feed?.created_by?.id === currentUser.id;
    const isFolderOwner = parentFolder?.owner?.id === currentUser.id;
    const hasEditAndReshareAccess =
      feed?.highest_user_access?.access?.title === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE;

    return isFeedOwner || isFolderOwner || isFolderCollaborator || hasEditAndReshareAccess;
  };

  const canDeleteAngle = (feed: VIDEOFEED) => {
    if (!showSidebar || !currentUser) {
      return false;
    }

    const isFeedOwner = String(feed?.created_by?.id) === String(currentUser.id);
    const isFolderOwner =
      String(parentFolder?.owner?.id) === String(currentUser.id);
    const isOrganizationAdmin =
      currentUser.role?.title === RolesLowerCase.ORGANIZATION_ADMIN &&
      Number(feed.organization_id) === Number(currentUser.organization?.id);
    const hasEditAndReshareAccess =
      feed?.highest_user_access?.access?.title ===
      ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE;

    return (
      feed?.type === VIDEOFEED_TYPE.TIMELINE &&
      feed?.status === VIDEOFEED_STATUS.DONE &&
      currentUser?.role?.title !== RolesLowerCase.VIEWER &&
      (feed?.videos?.length || 0) > 1 &&
      (isFeedOwner ||
        isFolderOwner ||
        isOrganizationAdmin ||
        hasEditAndReshareAccess)
    );
  };

  const getPendingAdditionalAngleUploadsByFeedId = (feedId: string | number) =>
    uploadingContentState.filter(
      (item) =>
        String(item?.videofeedId) === String(feedId) &&
        item?.uploadMode === 'additional-angle' &&
        item?.status !== UploadingStatus.FAILED &&
        item?.status !== UploadingStatus.FINISHED,
    );

  const canUploadAdditionalAngles = (feed: VIDEOFEED) => {
    const pendingUploads = getPendingAdditionalAngleUploadsByFeedId(feed.id);
    const hasUploadingAngle = pendingUploads.some((item) => item.status === UploadingStatus.UPLOADING);
    const hasPendingAngle = pendingUploads.length > 0;
    const collectionFeeds = [feed, ...(feed.reference_timelines || [])];
    const collectionVideos = collectionFeeds.flatMap((item) => item.videos || []);
    const existingAngleCount = Math.max(
      ...collectionFeeds.map((item) => item.videos?.length || 0),
    );
    const pendingNotInFeed = pendingUploads.filter(
      (item) => !item.videoId || !collectionVideos.some((video) => String(video.id) === String(item.videoId)),
    ).length;

    return (
      feed?.type !== VIDEOFEED_TYPE.COMPOSER &&
      feed?.type !== VIDEOFEED_TYPE.MY_COMPOSER &&
      (feed?.status === VIDEOFEED_STATUS.DONE || hasPendingAngle) &&
      (existingAngleCount + pendingNotInFeed < 4 || hasUploadingAngle) &&
      (isContentAdminOA || canRenameAngle(feed))
    );
  };

  const getPendingAdditionalAngleUploads = (feed: VIDEOFEED) => {
    const targetFeedIds = new Set([String(feed.id)]);
    videoFeed.forEach((collection) => {
      if ((collection.reference_timelines || []).some((timeline) => String(timeline.id) === String(feed.id))) {
        targetFeedIds.add(String(collection.id));
      }
    });
    const videoIds = new Set(
      (feed.videos || [])
        .filter(
          (video) =>
            feed.status === VIDEOFEED_STATUS.DONE ||
            String((video as any).status || '').toUpperCase() === 'TRANSCODED',
        )
        .map((video) => String(video.id)),
    );
    return uploadingContentState.filter(
      (item) =>
        targetFeedIds.has(String(item?.videofeedId)) &&
        item?.uploadMode === 'additional-angle' &&
        item?.status !== UploadingStatus.FAILED &&
        (!item.videoId || !videoIds.has(String(item.videoId))),
    );
  };

  const getVisibleAngleCount = (feed: VIDEOFEED) => {
    const pendingUploads = getPendingAdditionalAngleUploads(feed);
    const pendingVideoIds = new Set(pendingUploads.map((item) => String(item.videoId || '')));
    return feed.videos.filter((video) => !pendingVideoIds.has(String(video.id))).length + pendingUploads.length;
  };

  const openAdditionalAngleUpload = (feedId?: string) => {
    if (!feedId) {
      return;
    }

    const feed = sidebarFeedById.get(feedId) || videoFeed.find((item) => String(item.id) === String(feedId)) || null;
    if (feed) {
      dispatch(updateAdditionalAngleUploadFeed(feed));
    }
  };

  const openAdditionalAngleMenu = (e: React.MouseEvent, feed: VIDEOFEED) => {
    if (!canUploadAdditionalAngles(feed)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const menuWidth = 220;
    const menuHeight = 44;
    setAngleContextMenu({
      mode: 'additional-angle',
      feedId: String(feed.id),
      videoId: '',
      x: e.clientX + menuWidth > window.innerWidth ? window.innerWidth - menuWidth - 8 : e.clientX,
      y: e.clientY + menuHeight > window.innerHeight ? window.innerHeight - menuHeight - 8 : e.clientY,
    });
  };

  const refreshCurrentFolderData = async () => {
    if (!folderId) {
      return;
    }

    if (shared) {
      await fetchSharedVideoData(folderId);
      return;
    }

    await fetchVideoData(folderId);
  };

  const startAngleRename = (video: VIDEO_TYPE) => {
    setAngleContextMenu(null);
    setEditingVideoId(String(video.id));
    setEditingAngleName(video.name || '');
  };

  const cancelAngleRename = (video?: VIDEO_TYPE) => {
    setEditingVideoId(null);
    setEditingAngleName(video?.name || '');
    setSavingVideoId(null);
  };

  const saveAngleRename = async (video: VIDEO_TYPE) => {
    const nextName = editingAngleName.trim().slice(0, 255);

    if (!nextName) {
      notification.showMessage(NOTIFICATION.EMPTY);
      return;
    }

    if (nextName === (video.name || '').trim()) {
      cancelAngleRename(video);
      return;
    }

    setSavingVideoId(String(video.id));

    try {
      await updateVideoTitle(String(video.id), nextName);
      notification.showMessage(NOTIFICATION.SUCCESSFUL_NAME_CHANGE);
      setEditingVideoId(null);
      await refreshCurrentFolderData();
    } catch (err: any) {
      if (err?.response?.data?.message) {
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.NOT_EDITED);
      }
    } finally {
      setSavingVideoId(null);
    }
  };

  const handleDeleteAngle = async () => {
    if (!angleToDelete || deletingAngle) {
      return;
    }

    const feed =
      sidebarFeedById.get(angleToDelete.feedId) ||
      videoFeed.find((item) => String(item.id) === angleToDelete.feedId);
    const deletedIndex = feed?.videos?.findIndex(
      (video) => String(video.id) === angleToDelete.videoId,
    ) ?? -1;

    if (!feed || deletedIndex < 0 || !canDeleteAngle(feed)) {
      setAngleToDelete(null);
      return;
    }

    const activeFeed =
      sidebarFeedById.get(String(selectedFeed)) ||
      videoFeed.find((item) => String(item.id) === String(selectedFeed));
    const activeVideos = activeFeed?.videos || [];
    const activeDeletedIndex = activeVideos.findIndex(
      (video) => String(video.id) === angleToDelete.videoId,
    );
    const storedVideoId = String(storeSelectedClip?.id || '');
    const selectedVideoId = activeVideos.some(
      (video) => String(video.id) === storedVideoId,
    )
      ? storedVideoId
      : String(activeVideos[selectedClip]?.id || '');
    const remainingActiveVideos = activeVideos.filter(
      (video) => String(video.id) !== angleToDelete.videoId,
    );
    const nextVideo =
      activeDeletedIndex >= 0
        ? remainingActiveVideos.find(
            (video) => String(video.id) === selectedVideoId,
          ) ||
          remainingActiveVideos[
            Math.min(activeDeletedIndex, remainingActiveVideos.length - 1)
          ]
        : null;

    setDeletingAngle(true);
    try {
      await deleteVideoAngle(angleToDelete.feedId, angleToDelete.videoId);
      await refreshCurrentFolderData();

      if (nextVideo) {
        const nextIndex = remainingActiveVideos.findIndex(
          (video) => String(video.id) === String(nextVideo.id),
        );
        const keepTime =
          videoPlayedSeconds || videoControls.playedSeconds || 0;
        if (keepTime > 0) {
          dispatch(updateVideoPlayedSeconds(keepTime));
          dispatch(updateTimelineSeconds(keepTime));
          dispatch(
            updateVideoControls({
              ...videoControls,
              playedSeconds: keepTime,
            }),
          );
        }
        const paramsObj = parseQueryString(window.location.search);
        paramsObj.clip = String(nextIndex);
        paramsObj.clipId = nextVideo.id;
        window.history.replaceState(null, '', `?${buildQueryString(paramsObj)}`);
        setSelectedClip(nextIndex);
        dispatch(saveClipToStore(nextVideo));
        dispatch(triggerRefresh());
      }

      setAngleToDelete(null);
      bypassCacheForNextVideofeedsListFetch();
      dispatch(refetchFeeds());
      notification.showMessage('Angle deleted');

      try {
        const organization = await getUserOrganization();
        dispatch(saveMyOrganization(organization.data));
      } catch (error) {
        console.error(error);
      }
    } catch (err: any) {
      notification.showMessage(
        err?.response?.data?.message || NOTIFICATION.NOT_DELETED,
      );
    } finally {
      setDeletingAngle(false);
    }
  };

  useOnClickOutside(angleMenuRef, () => {
    setAngleContextMenu(null);
  });

  const handleScrollToFeed = (feedId: string) => {
    if (feedRefs.current[feedId]) {
      const element = feedRefs.current[feedId];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  useEffect(() => {
    setSelectedFeed(feedId as string);
    const clipInd = findSearchValue('clip');
    if (clipInd) {
      setSelectedClip(+clipInd);
    }
    const scrollTimeout =
      feedId && shouldAutoScroll
        ? window.setTimeout(() => {
            handleScrollToFeed(feedId);
          }, 1500)
        : undefined;

    return () => window.clearTimeout(scrollTimeout);
  }, [feedId, videoFeed]);

  const [selectedFeed, setSelectedFeed] = useState('');
  const [selectedClip, setSelectedClip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState('');

  const sidebarGroups = useMemo(() => {
    type TDoneFeed = (typeof doneFeeds)[number];
    type TSidebarGroup = {
      key: string;
      rootFeed: TDoneFeed;
      feeds: TDoneFeed[];
      isCollection: boolean;
    };

    const groups: TSidebarGroup[] = [];
    let collectionGroup: TSidebarGroup | null = null;

    groupedSidebarFeeds.items.forEach((item) => {
      if (item.kind === 'collection') {
        collectionGroup = {
          key: item.key,
          rootFeed: item.feed,
          feeds: [],
          isCollection: true,
        };
        groups.push(collectionGroup);
        return;
      }

      if (item.belongsToCollection && collectionGroup) {
        collectionGroup.feeds.push(item.feed);
        return;
      }

      collectionGroup = null;
      groups.push({
        key: item.key,
        rootFeed: item.feed,
        feeds: [item.feed],
        isCollection: false,
      });
    });

    return groups;
  }, [groupedSidebarFeeds.items, doneFeeds]);

  const effectiveSelectedFeed = selectedFeed || String(feedId || '');
  const currentSidebarGroup =
    sidebarGroups.find(
      (group) =>
        String(group.rootFeed.id) === effectiveSelectedFeed ||
        group.feeds.some((feed) => String(feed.id) === effectiveSelectedFeed),
    ) || sidebarGroups[0];
  const currentAngleFeed =
    currentSidebarGroup?.feeds.find((feed) => String(feed.id) === effectiveSelectedFeed) ||
    currentSidebarGroup?.feeds[0] ||
    currentSidebarGroup?.rootFeed;
  const otherSidebarGroups = sidebarGroups.filter(
    (group) =>
      group.key !== currentSidebarGroup?.key &&
      (group.rootFeed.collection_name || group.rootFeed.title || '').toLowerCase().includes(sidebarSearch.trim().toLowerCase()),
  );

  useEffect(() => {
    if (editingVideoId && angleInputRef.current) {
      angleInputRef.current.focus();
      angleInputRef.current.select();
    }
  }, [editingVideoId]);

  useEffect(() => {
    setAngleContextMenu(null);
    setEditingVideoId(null);
    setSavingVideoId(null);
  }, [selectedFeed]);

  const fetchVideoData = async (id: string) => {
    setLoading(true);
    try {
      await dispatch(getAllVideoFeedsByFolderId({ folderId: id }));
      await dispatch(getFolderByIdThunk(id));
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage('You dont have access for this content');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedVideoData = async (id: string) => {
    const page = findSearchValue('page');
    const limit = findSearchValue('limit');
    const sort = findSearchValue('sort') === 'undefined' ? '' : findSearchValue('sort');
    const search = findSearchValue('search') === 'undefined' ? '' : findSearchValue('search');
    let sortRes = allContent ? getAllContentSortOptionAndType() : getSharedWithMePageSortOptionAndType();
    if (!sortRes) {
      sortRes = 'asc/name';
    }
    setLoading(true);
    try {
      if (sortRes) {
        const sortAndOption = sortRes.split('/');
        if (sortAndOption[0] && sortAndOption[1]) {
          const sortTypeQuery = sortAndOption[0] ? sortAndOption[0] : '';
          const sortFieldQuery = sortAndOption[1] ? sortAndOption[1] : '';
          if (!allContent) {
            await dispatch(
              getAllSharedVideoFeedsByFolderId({
                folderId: id,
                sort: sortTypeQuery as SortType,
                sortOption: sortFieldQuery as SortOptions,
              }),
            );
          } else {
            await dispatch(
              getAllContentVideofeedsThunk({ folderId: id, sort: sortTypeQuery as SortType, sortOption: sortFieldQuery as SortOptions }),
            );
          }
        } else {
          await dispatch(getAllSharedVideoFeedsByFolderId({ folderId: id, sort: SortType.ASC, sortOption: SortOptions.NAME }));
        }
      } else {
        await dispatch(getAllSharedVideoFeedsByFolderId({ folderId: id, sort: SortType.ASC, sortOption: SortOptions.NAME }));
      }
      if (allContent) {
        await dispatch(
          getAllContentFolderThunk({
            folderId: id,
            // @ts-ignore
            page: +page === +page ? +page : 0,
            // @ts-ignore
            limit: +limit === +limit ? +limit : 500,
            // @ts-ignore
            sort: sort ? sort : 'asc',
            search: search ? search : '',
            sortOption: SortOptions.NAME,
          }),
        );
      } else {
        await dispatch(
          getSharedFolderByIdThunk({
            folderId: id,
            // @ts-ignore
            page: +page === +page ? +page : 0,
            // @ts-ignore
            limit: +limit === +limit ? +limit : 500,
            // @ts-ignore
            sort: sort ? sort : 'asc',
            search: search ? search : '',
            sortOption: SortOptions.NAME,
          }),
        );
      }
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage('You dont have access for this content');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (folderId && shared) {
      fetchSharedVideoData(folderId);
    } else if (folderId) {
      fetchVideoData(folderId);
    }
  }, [folderId, shared, refetchFeedList]);

  useEffect(() => {
    return () => {
      dispatch(clearVideoFeeds());
      dispatch(removeStoreSelectedClip());
    };
  }, [folderId, shared]);

  useEffect(() => {
    const clipInd = findSearchValue('clip');
    if (clipInd && Number(clipInd) !== selectedClip) return;

    if (videoFeed.length && selectedFeed) {
      const selected = sidebarFeedById.get(String(selectedFeed)) || videoFeed.find((f) => f.id === selectedFeed);

      if (selected?.videos[selectedClip]) {
        dispatch(saveClipToStore(selected.videos[selectedClip]));
      }
    }
  }, [videoFeed, refreshTrigger, selectedClip, selectedFeed, sidebarFeedById]);

  useEffect(() => {
    const activeVideos = composerVideoClips?.length ? composerVideoClips : currentComposerVideos;
    const playingAngleIndex = location.pathname === PATHS.COMPOSER_PAGE.path
      ? activeVideos.findIndex((video) => video && String(video.id) === String(currentVideoThatInFrame?.id))
      : -1;
    // Automatic locks update the player angle independently of the sidebar's URL refresh.
    setSelectedClip(playingAngleIndex >= 0 ? playingAngleIndex : Number(findSearchValue('clip') || 0));
  }, [refreshTrigger, composerRefreshTrigger, currentVideoThatInFrame, composerVideoClips, currentComposerVideos, location.pathname]);

  useEffect(() => {
    if (!showSidebar || !currentAngleFeed) return;

    const angle = angleRefs.current[`${currentAngleFeed.id}-${selectedClip}`];
    const angleList = angle?.parentElement;
    if (!angle || !angleList) return;

    const angleRect = angle.getBoundingClientRect();
    const listRect = angleList.getBoundingClientRect();
    const left = angleList.scrollLeft + angleRect.left - listRect.left - (angleList.clientWidth - angleRect.width) / 2;

    angleList.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [currentAngleFeed, selectedClip, showSidebar]);

  const onPanelChange = (e: React.MouseEvent<HTMLDivElement>) => {
    if (editingVideoId) {
      return;
    }
    setAngleContextMenu(null);
    setShouldAutoScroll(false);
    if (e.currentTarget.id !== selectedFeed) {
      dispatch(clearComposerData());
      dispatch(updateNotes([]));
      dispatch(updateQualifiers([]));
      dispatch(updateProgressPosition(0));
      dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playedSeconds: 0 }));
      dispatch(updateComposerPixelPosition(0));
      const paramsObj = parseQueryString(window.location.search);
      const selected = sidebarFeedById.get(String(e.currentTarget.id)) || videoFeed.find((f) => f.id === e.currentTarget.id);
      const selectedIndex =
        sidebarFeedIndexById[String(e.currentTarget.id)] ?? videoFeed.findIndex((f) => f.id === e.currentTarget.id);
      if ((selected && selected.type === VIDEOFEED_TYPE.TIMELINE) || (selected && !selected.type)) {
        paramsObj.folderId = folderId as string;
        paramsObj.feedId = selected.id as string;
        paramsObj.feed = `${selectedIndex}`;
        paramsObj.clipId = selected.videos[0].id;
        paramsObj.clip = '0';

        history.push(`${PATHS.TIMELINE.path}?${buildQueryString(paramsObj)}`);
      } else if (selected && selected.type === VIDEOFEED_TYPE.COMPOSER) {
        paramsObj.folderId = folderId as string;
        paramsObj.feedId = selected.id as string;
        paramsObj.feed = `${selectedIndex}`;
        paramsObj.clipId = selected.videos[0].id;
        paramsObj.clip = '0';
        history.push(`${PATHS.COMPOSER_PAGE.path}?${buildQueryString(paramsObj)}`);
      }
      // change the search property of the main url
      location.search = searchParams.toString();
      setSelectedFeed(e.currentTarget.id);
      setSelectedClip(0);
      dispatch(updateNotes([]));
      dispatch(updateQualifiers([]));
      dispatch(updateSortedSelectedClips([]));
      dispatch(emptyComposerStackAction());
      if (selected) {
        dispatch(saveClipToStore(selected.videos[0]));
      }
      dispatch(triggerRefresh());
      dispatch(updateVideoPlayedSeconds(0));
    }
  };

  const onClipChange = (e: React.MouseEvent<HTMLDivElement>) => {
    if (editingVideoId) {
      return;
    }
    setAngleContextMenu(null);
    setShouldAutoScroll(false);
    if (Number(e.currentTarget.id) !== selectedClip) {
      const keepTime = videoPlayedSeconds || videoControls.playedSeconds || 0;
      if (keepTime > 0) {
        dispatch(updateVideoPlayedSeconds(keepTime));
        dispatch(updateVideoControls({ ...videoControls, playedSeconds: keepTime }));
      }

      setSelectedClip(Number(e.currentTarget.id));
      const selected = sidebarFeedById.get(String(selectedFeed)) || videoFeed.find((f) => f.id === selectedFeed);
      if (selected) {
        dispatch(saveClipToStore(selected.videos[Number(e.currentTarget.id)]));
        const paramsObj = parseQueryString(window.location.search);
        paramsObj.folderId = folderId as string;
        paramsObj.feedId = selected.id;
        paramsObj.clipId = selected.videos[Number(e.currentTarget.id)].id;
        paramsObj.clip = `${Number(e.currentTarget.id)}`;

        window.history.replaceState(null, '', `?${buildQueryString(paramsObj)}`);
        dispatch(location.pathname === PATHS.COMPOSER_PAGE.path ? triggerComposerRefresh() : triggerRefresh());
      }
    }
  };

  const renderAngleList = (feed: (typeof doneFeeds)[number], featured = false) => {
    const pendingUploads = getPendingAdditionalAngleUploads(feed);
    const pendingVideoIds = new Set(pendingUploads.map((item) => String(item.videoId || '')));

    return (
      <div className={cx(styles.cliplist, featured && styles.angleGrid, !showSidebar && styles.cliplistCompact)}>
        {[...feed.videos]
        .filter((video) => !pendingVideoIds.has(String(video.id)))
        .slice()
        .sort((a, b) => a?.angle - b?.angle)
        .map((video, index) => {
          const isEditingAngle = editingVideoId === String(video.id);
          const videoIndex = feed.videos.findIndex((item) => String(item.id) === String(video.id));

          return (
            <div
              key={video.id}
              ref={featured ? (element) => (angleRefs.current[`${feed.id}-${videoIndex}`] = element) : undefined}
              className={cx(
                selectedClip === videoIndex ? styles.activeClip : styles.clip,
                featured && styles.angleCard,
                isEditingAngle && styles.clipEditing,
                !showSidebar && styles.clipCompact,
              )}
              onClick={onClipChange}
              onContextMenu={(e) => {
                if (
                  editingVideoId ||
                  (!canRenameAngle(feed) && !canDeleteAngle(feed))
                ) {
                  return;
                }

                e.preventDefault();
                e.stopPropagation();

                const menuWidth = 132;
                const menuHeight =
                  (Number(canRenameAngle(feed)) + Number(canDeleteAngle(feed))) *
                  44;
                const nextX =
                  e.clientX + menuWidth > window.innerWidth
                    ? window.innerWidth - menuWidth - 8
                    : e.clientX;
                const nextY =
                  e.clientY + menuHeight > window.innerHeight
                    ? window.innerHeight - menuHeight - 8
                    : e.clientY;

                setAngleContextMenu({
                  mode: 'rename',
                  feedId: String(feed.id),
                  videoId: String(video.id),
                  x: nextX,
                  y: nextY,
                });
              }}
              id={`${videoIndex}`}
            >
              {showSidebar ? (
                isEditingAngle ? (
                  <div
                    className={styles.clipEditor}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <input
                      ref={angleInputRef}
                      className={styles.clipInput}
                      value={editingAngleName}
                      disabled={savingVideoId === String(video.id)}
                      onChange={(e) => {
                        setEditingAngleName(e.target.value);
                      }}
                      onBlur={() => {
                        if (savingVideoId === String(video.id)) {
                          return;
                        }
                        cancelAngleRename(video);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void saveAngleRename(video);
                        }

                        if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelAngleRename(video);
                        }
                      }}
                    />
                  </div>
                ) : featured ? (
                  <>
                    <div className={styles.angleThumbnail}>
                      <MovieIcon />
                      {video.s3_preview_url && <img src={video.s3_preview_url} alt="" onError={(e) => (e.currentTarget.style.display = 'none')} />}
                      <span>{index + 1}</span>
                    </div>
                    <div className={styles.angleName}>{video.name ? video.name : `Angle ${index + 1}`}</div>
                  </>
                ) : (
                  <div>{video.name ? video.name : `Angle ${index + 1}`}</div>
                )
              ) : (
                index + 1
              )}
            </div>
          );
        })}
        {pendingUploads.map((content, index) => (
          <div
            key={`pending-angle-${feed.id}-${content.id}-${index}`}
            className={cx(styles.clip, styles.clipPending, featured && styles.angleCard, !showSidebar && styles.clipCompact)}
            style={{ cursor: 'default', pointerEvents: 'none' }}
          >
            {featured && showSidebar && <div className={styles.anglePending}>Processing</div>}
            {showSidebar ? <div className={styles.angleName}>{content.name || 'Additional angle'}</div> : '…'}
          </div>
        ))}
      </div>
    );
  };

  const angleContextFeed =
    angleContextMenu?.mode === 'rename'
      ? sidebarFeedById.get(String(angleContextMenu.feedId || selectedFeed)) ||
        videoFeed.find(
          (feed) =>
            String(feed.id) ===
            String(angleContextMenu.feedId || selectedFeed),
        )
      : null;
  const angleContextVideo = angleContextFeed?.videos?.find(
    (video) => String(video.id) === angleContextMenu?.videoId,
  );

  return (
    <div className={cx(styles.fileListContainer, !showSidebar && styles.compact)}>
      <div className={styles.wrapper}>
        {(status === STATUS.LOADING || loading) && <LinearProgress />}
        {(status === STATUS.LOADING || loading) && !videoFeed.length && <FullPageLoader loading />}
        {videoFeed.length > 0 && (
          <>
            {showSidebar && parentFolder?.name && (
              <div className={styles.title}>
                <img src={parentFolder.thumbnail_url || FolderIcon} alt="" />
                <span>{parentFolder.name}</span>
              </div>
            )}
            <div className={cx(styles.fileList, !showSidebar && styles.fileListCompact, 'CustomScrollbar')}>
              {showSidebar && currentSidebarGroup && currentAngleFeed && (
                <div className={styles.currentFileCard}>
                  <div className={styles.currentFileLabel}>Current file</div>
                  <div
                    className={styles.currentFileHeader}
                    onContextMenu={(e) => openAdditionalAngleMenu(e, currentSidebarGroup.rootFeed)}
                  >
                    {currentSidebarGroup.rootFeed.thumbnail_url ? (
                      <img
                        src={currentSidebarGroup.rootFeed.thumbnail_url}
                        alt=""
                        className={styles.currentFileThumbnail}
                      />
                    ) : currentSidebarGroup.rootFeed.type === VIDEOFEED_TYPE.COMPOSER ||
                      currentSidebarGroup.rootFeed.type === VIDEOFEED_TYPE.MY_COMPOSER ? (
                      <ComposerIcon className={styles.currentFileIcon} />
                    ) : (
                      <MovieIcon className={styles.currentFileIcon} />
                    )}
                    <Tooltip title={currentSidebarGroup.rootFeed.collection_name || currentSidebarGroup.rootFeed.title} arrow>
                      <div className={styles.currentFileTitle}>
                        {currentSidebarGroup.rootFeed.collection_name || currentSidebarGroup.rootFeed.title}
                      </div>
                    </Tooltip>
                    {canUploadAdditionalAngles(currentSidebarGroup.rootFeed) && (
                      <button
                        type="button"
                        className={styles.currentFileMenu}
                        aria-label="Current file actions"
                        onClick={(e) => openAdditionalAngleMenu(e, currentSidebarGroup.rootFeed)}
                      >
                        <MoreVertIcon />
                      </button>
                    )}
                  </div>

                  <div className={styles.currentFileSection}>
                    <div className={styles.sectionHeading}>
                      <span>Video angles</span>
                      <span>{getVisibleAngleCount(currentAngleFeed)}</span>
                    </div>
                    {renderAngleList(currentAngleFeed, true)}
                  </div>

                  <div className={styles.currentFileSection}>
                    <div className={styles.sectionHeading}>
                      <span>
                        {currentSidebarGroup.rootFeed.type === VIDEOFEED_TYPE.COMPOSER ||
                        currentSidebarGroup.rootFeed.type === VIDEOFEED_TYPE.MY_COMPOSER
                          ? 'Composer'
                          : 'Timelines'}
                      </span>
                      <span>{currentSidebarGroup.feeds.length}</span>
                    </div>
                    <div className={styles.timelineList}>
                      {currentSidebarGroup.feeds.map((feed) => (
                        <div
                          key={`current-timeline-${feed.id}`}
                          id={String(feed.id)}
                          ref={(el) => (feedRefs.current[String(feed.id)] = el)}
                          className={cx(styles.timelineItem, effectiveSelectedFeed === String(feed.id) && styles.timelineItemActive)}
                          onClick={onPanelChange}
                        >
                          {feed.type === VIDEOFEED_TYPE.COMPOSER || feed.type === VIDEOFEED_TYPE.MY_COMPOSER ? (
                            feed.thumbnail_url ? (
                              <img src={feed.thumbnail_url} alt="" className={styles.composerThumbnail} />
                            ) : (
                              <ComposerIcon />
                            )
                          ) : (
                            <TimelineIcon />
                          )}
                          <Tooltip title={feed.title} arrow>
                            <span>{feed.title}</span>
                          </Tooltip>
                          <ChevronRightIcon />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {showSidebar && (
                <div className={styles.otherFilesCard}>
                  <div className={styles.otherFilesHeader}>
                    <span>Other files</span>
                    <input
                      type="search"
                      value={sidebarSearch}
                      placeholder="Search"
                      aria-label="Search other files"
                      onChange={(e) => setSidebarSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === ' ') e.stopPropagation(); }}
                    />
                  </div>
                  <div className={styles.otherFilesList}>
                    {otherSidebarGroups.map((group) => {
                      const targetFeed = group.feeds[0] || group.rootFeed;
                      const angleCount = Math.max(
                        group.rootFeed.videos?.length || 0,
                        ...group.feeds.map((feed) => feed.videos?.length || 0),
                      );
                      const timelineCount = group.isCollection
                        ? group.feeds.length
                        : group.rootFeed.type === VIDEOFEED_TYPE.TIMELINE
                        ? 1
                        : 0;

                      return (
                        <div
                          key={`other-${group.key}`}
                          id={String(targetFeed.id)}
                          className={styles.otherFileItem}
                          onClick={onPanelChange}
                          onContextMenu={(e) => openAdditionalAngleMenu(e, group.rootFeed)}
                        >
                          {group.rootFeed.type === VIDEOFEED_TYPE.COMPOSER ||
                          group.rootFeed.type === VIDEOFEED_TYPE.MY_COMPOSER ? (
                            group.rootFeed.thumbnail_url ? (
                              <img src={group.rootFeed.thumbnail_url} alt="" className={styles.composerThumbnail} />
                            ) : (
                              <ComposerIcon className={styles.otherFileIcon} />
                            )
                          ) : (
                            <MovieIcon className={styles.otherFileIcon} />
                          )}
                          <div className={styles.otherFileDetails}>
                            <Tooltip title={group.rootFeed.collection_name || group.rootFeed.title} arrow>
                              <div>{group.rootFeed.collection_name || group.rootFeed.title}</div>
                            </Tooltip>
                            <span>
                              {angleCount} {angleCount === 1 ? 'angle' : 'angles'}
                              {timelineCount > 0 && ` · ${timelineCount} ${timelineCount === 1 ? 'timeline' : 'timelines'}`}
                            </span>
                          </div>
                          <ChevronRightIcon className={styles.otherFileArrow} />
                        </div>
                      );
                    })}
                    {otherSidebarGroups.length === 0 && <div className={styles.noOtherFiles}>No other files</div>}
                  </div>
                </div>
              )}

              {!showSidebar && groupedSidebarFeeds.items.map((item) => {
                  const feed = item.feed;
                  if (item.kind === 'collection') {
                    const collectionTitle = feed.collection_name || feed.title;
                    const firstTimelineId = String(feed?.reference_timelines?.[0]?.id || feed.id);
                    const isSelectedCollection =
                      selectedFeed === firstTimelineId || selectedFeed === String(feed.id);
                    const selectedCollectionFeed = sidebarFeedById.get(firstTimelineId) || feed;
                    const hasReferenceTimelines = firstTimelineId !== String(feed.id);
                    return (
                      <div
                        key={item.key}
                        className={cx(styles.accordion, isSelectedCollection && styles.activeCollectionColor)}
                        ref={(el) => (feedRefs.current[firstTimelineId] = el)}
                      >
                        <div
                          onClick={onPanelChange}
                          onContextMenu={(e) => {
                            if (!canUploadAdditionalAngles(feed)) {
                              return;
                            }

                            e.preventDefault();
                            e.stopPropagation();

                            const menuWidth = 220;
                            const menuHeight = 44;
                            const nextX = e.clientX + menuWidth > window.innerWidth ? window.innerWidth - menuWidth - 8 : e.clientX;
                            const nextY = e.clientY + menuHeight > window.innerHeight ? window.innerHeight - menuHeight - 8 : e.clientY;

                            setAngleContextMenu({
                              mode: 'additional-angle',
                              feedId: String(feed.id),
                              videoId: '',
                              x: nextX,
                              y: nextY,
                            });
                          }}
                          className={cx(styles.fileItem, styles.collectionFeedItem, !showSidebar && styles.fileItemCompact)}
                          id={firstTimelineId}
                        >
                          <Tooltip
                            title={
                              <div>
                                title: {collectionTitle}
                                <div style={{ margin: '6px  0' }} />
                              </div>
                            }
                            arrow
                            placement="right"
                          >
                            <div className={cx(styles.leftSide, !showSidebar && styles.leftSideCompact)}>
                              <MovieIcon className={styles.fileIcon} />
                              {showSidebar && <div className={styles.fileTitle}>{collectionTitle}</div>}
                            </div>
                          </Tooltip>
                          <div className={styles.rightSide} style={{ marginRight: 5 }}>
                            {isSelectedCollection ? <OpenedIcon className={styles.activeCollectionColor} /> : <ClosedIcon />}
                          </div>
                        </div>
                        {isSelectedCollection && !hasReferenceTimelines && renderAngleList(selectedCollectionFeed)}
                      </div>
                    );
                  }

                  const isReference = item.isReference;
                  const isCollectionParent = !isReference && groupedSidebarFeeds.parentIds.has(String(feed.id));
                  const feedTitle = isCollectionParent ? feed.collection_name || feed.title : feed.title;
                  const tooltipContent = (
                    <div>
                      title: {feedTitle}
                      <div style={{ margin: '6px  0' }} />
                      angles:
                      {feed.videos.map((v, index) => (
                        <div key={`clip_sidebar_${v.id}`}>
                          <div style={{ marginBottom: '6px' }} />
                          {v.name ? v.name : `Angle ${index + 1}`}
                        </div>
                      ))}
                      <div style={{ margin: '6px  0' }} />
                    </div>
                  );
                  return (
                    <div
                      key={item.key}
                      className={cx(styles.accordion, selectedFeed === String(feed.id) && styles.activeColor)}
                      ref={(el) => (feedRefs.current[String(feed.id)] = el)}
                    >
                      <div
                        onClick={onPanelChange}
                        onContextMenu={(e) => {
                          if (isReference || !canUploadAdditionalAngles(feed)) {
                            return;
                          }

                          e.preventDefault();
                          e.stopPropagation();

                          const menuWidth = 220;
                          const menuHeight = 44;
                          const nextX = e.clientX + menuWidth > window.innerWidth ? window.innerWidth - menuWidth - 8 : e.clientX;
                          const nextY = e.clientY + menuHeight > window.innerHeight ? window.innerHeight - menuHeight - 8 : e.clientY;

                          setAngleContextMenu({
                            mode: 'additional-angle',
                            feedId: String(feed.id),
                            videoId: '',
                            x: nextX,
                            y: nextY,
                          });
                        }}
                        className={cx(
                          styles.fileItem,
                          item.belongsToCollection && styles.referenceFeedItem,
                          !showSidebar && styles.fileItemCompact,
                        )}
                        id={String(feed.id)}
                      >
                        <Tooltip title={tooltipContent} arrow placement="right">
                          <div className={cx(styles.leftSide, !showSidebar && styles.leftSideCompact)}>
                            {feed.status !== VIDEOFEED_STATUS.TRANSCODING && feed.status !== VIDEOFEED_STATUS.DONE ? (
                              <NotReadyIcon className={styles.notReadyIcon} />
                            ) : feed?.type === VIDEOFEED_TYPE.MY_COMPOSER || feed?.type === VIDEOFEED_TYPE.COMPOSER ? (
                              feed.thumbnail_url ? (
                                <img
                                  src={feed.thumbnail_url}
                                  alt=""
                                  className={cx(styles.fileIcon, styles.composerThumbnail)}
                                />
                              ) : (
                                <ComposerIcon className={styles.fileIcon} />
                              )
                            ) : isReference ? (
                              <TimelineIcon className={styles.fileIcon} style={{ color: '#39b3ea' }} />
                            ) : (
                              <MovieIcon className={styles.fileIcon} />
                            )}
                            {showSidebar && <div className={styles.fileTitle}>{feedTitle}</div>}
                          </div>
                        </Tooltip>
                        <div className={styles.rightSide} style={{ marginRight: 5 }}>
                          {selectedFeed === String(feed.id) ? <OpenedIcon className={styles.activeColor} /> : <ClosedIcon />}
                        </div>
                      </div>

                      {selectedFeed === String(feed.id) && renderAngleList(feed)}
                    </div>
                  );
                })}
            </div>
          </>
        )}
      </div>
      {angleContextMenu &&
        createPortal(
          <div
            ref={angleMenuRef}
            className={styles.clipMenu}
            style={{ top: angleContextMenu.y, left: angleContextMenu.x }}
          >
            {angleContextMenu.mode === 'additional-angle' ? (
              <button
                type="button"
                className={styles.clipMenuItem}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openAdditionalAngleUpload(angleContextMenu.feedId);
                  setAngleContextMenu(null);
                }}
              >
                <span style={{ display: 'inline-flex', width: 24, flexShrink: 0, alignItems: 'center', justifyContent: 'center' }}>
                  <AddIcon fontSize="small" />
                </span>
                <span>Upload additional Angle/s</span>
              </button>
            ) : (
              <>
                {angleContextFeed &&
                  angleContextVideo &&
                  canRenameAngle(angleContextFeed) && (
                    <button
                      type="button"
                      className={styles.clipMenuItem}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        startAngleRename(angleContextVideo);
                      }}
                    >
                      Rename
                    </button>
                  )}
                {angleContextFeed &&
                  angleContextVideo &&
                  canDeleteAngle(angleContextFeed) && (
                    <button
                      type="button"
                      className={cx(
                        styles.clipMenuItem,
                        styles.clipMenuItemDanger,
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const angleIndex = angleContextFeed.videos.findIndex(
                          (video) =>
                            String(video.id) === String(angleContextVideo.id),
                        );
                        setAngleToDelete({
                          feedId: String(angleContextFeed.id),
                          videoId: String(angleContextVideo.id),
                          name:
                            angleContextVideo.name ||
                            `Angle ${angleIndex + 1}`,
                        });
                        setAngleContextMenu(null);
                      }}
                    >
                      Delete angle
                    </button>
                  )}
              </>
            )}
          </div>,
          document.body,
        )}
      <Modal
        open={!!angleToDelete}
        onClose={
          deletingAngle ? () => {} : () => setAngleToDelete(null)
        }
        loading={deletingAngle}
      >
        <div className={deleteModalStyles.modalBody}>
          <div className={deleteModalStyles.modalTitle}>
            Permanently delete “{angleToDelete?.name}”?
          </div>
          <div className={deleteModalStyles.modalSubtitle}>
            This angle and its video files will be removed immediately.
          </div>
          <div className={deleteModalStyles.restoreDisclaimer}>
            This cannot be undone. Upload hours will not be recovered.
          </div>
          <div className={deleteModalStyles.form}>
            <div className={deleteModalStyles.actionBtns}>
              <ContainedButton
                kind="red"
                disabled={deletingAngle}
                onClick={handleDeleteAngle}
              >
                Delete angle
              </ContainedButton>
              <OutlinedButton
                kind="default"
                disabled={deletingAngle}
                onClick={() => setAngleToDelete(null)}
              >
                Cancel
              </OutlinedButton>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VideoFileList;
